import { now } from '../Clock.ts';
import { GameEngine } from '../game/GameEngine.ts';
import type { GameConfig, GameState } from '../game/GameState.ts';
import type { Controller } from '../controllers/Controller.ts';
import { NullController } from '../controllers/KeyboardController.ts';
import { StimulationService } from '../stimulation/StimulationService.ts';
import type { SafetyLimits } from '../stimulation/SafetyGate.ts';
import { EventLogger, type LoggerOptions } from './EventLogger.ts';
import { EventType } from './Events.ts';
import { LatencyTracker } from './LatencyTracker.ts';

export interface SessionOptions {
  sessionId: string;
  game: Partial<GameConfig>;
  logger: Partial<LoggerOptions>;
  safety: Partial<SafetyLimits>;
  /** Rate at which plain state snapshots are logged, Hz. 0 disables. */
  stateSampleHz: number;
  /**
   * Cap on how much simulation time one `advance()` call may cover, ms.
   * Without this, a tab that was backgrounded for a minute would try to
   * simulate 7200 ticks in one frame and hang.
   */
  maxCatchUpMs: number;
}

export const DEFAULT_SESSION_OPTIONS: SessionOptions = {
  sessionId: '',
  game: {},
  logger: {},
  safety: {},
  stateSampleHz: 20,
  maxCatchUpMs: 250,
};

/**
 * One experimental session: engine + controller + logging + stimulation, and
 * the fixed-timestep loop that drives them.
 *
 * No DOM, no rendering, no timers. The browser app calls `advance()` from
 * requestAnimationFrame; `TrialRunner` calls it from a plain loop with a
 * synthetic clock and runs thousands of times faster than realtime. Both get
 * identical simulation behaviour, which is the only reason offline parameter
 * sweeps are worth anything.
 *
 * ## The two clocks
 *
 * Both matter, and conflating them is the easiest way to produce latency
 * numbers that look plausible and are wrong.
 *
 *   - The *simulation clock* (`tick`, `simTimeMs`) advances in exact 1/simHz
 *     steps. Physics, replay, and determinism depend on it, and it is the right
 *     x-axis for a trajectory plot.
 *   - The *wall clock* (`Clock.now()`) is when things really happened. Human
 *     response latency is only meaningful on this one.
 *
 * Every tick is stamped with the wall clock reading at the moment it is
 * simulated. When one animation frame has to catch up several ticks they share
 * a wall-clock timestamp, which is truthful: they really did all execute at
 * that instant. It also guarantees that an input edge is never stamped *after*
 * the flap it caused, which a nominal per-tick time cannot guarantee across a
 * frame boundary -- and a negative stage duration silently disappears from the
 * latency statistics rather than announcing itself.
 */
export class ExperimentSession {
  readonly engine: GameEngine;
  readonly logger: EventLogger;
  readonly latency: LatencyTracker;
  readonly stimulation: StimulationService;
  readonly options: SessionOptions;

  controller: Controller;
  paused = false;

  /** Extra per-frame pumps (simulated devices, stimulators). */
  private pumps: ((nowMs: number) => void)[] = [];

  private accumulatorMs = 0;
  private lastAdvanceAt: number | null = null;
  private lastSampleTick = -Infinity;
  private lastScore = 0;
  private wasAlive = true;
  private trialId: string | undefined;

  constructor(options: Partial<SessionOptions> = {}) {
    this.options = { ...DEFAULT_SESSION_OPTIONS, ...options };
    if (!this.options.sessionId) {
      this.options.sessionId = makeSessionId();
    }

    this.engine = new GameEngine(this.options.game);
    this.logger = new EventLogger(this.options.sessionId, this.options.logger, {
      gameConfig: { ...this.engine.config },
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'node',
    });
    this.latency = new LatencyTracker();
    this.logger.subscribe((e) => this.latency.ingest(e));
    this.stimulation = new StimulationService(this.logger, this.options.safety);
    this.controller = new NullController();

    // An emergency stop must also stop the thing generating the requests.
    this.stimulation.onEmergencyStop(() => {
      this.paused = true;
    });

    // The engine reads the wall clock through the same injectable `Clock` as
    // everything else, so a headless trial's virtual clock reaches it too.
    this.engine.now = () => now();

    this.logger.log(EventType.SESSION_START, {
      tick: 0,
      metadata: {
        sessionId: this.options.sessionId,
        seed: this.engine.config.seed,
        simHz: this.engine.config.simHz,
      },
    });
  }

  get dtMs(): number {
    return 1000 / this.engine.config.simHz;
  }

  get sessionId(): string {
    return this.options.sessionId;
  }

  registerPump(fn: (nowMs: number) => void): () => void {
    this.pumps.push(fn);
    return () => {
      this.pumps = this.pumps.filter((f) => f !== fn);
    };
  }

  setTrial(trialId: string | undefined): void {
    this.trialId = trialId;
  }

  async setController(next: Controller): Promise<void> {
    await this.controller.detach?.();
    this.controller = next;
    await next.attach?.();
    next.reset?.();
    this.logger.log(EventType.CONTROLLER_CHANGED, {
      tick: this.engine.currentTick,
      metadata: { controller: next.id, label: next.label },
    });
  }

  reset(seed = this.engine.config.seed): void {
    this.engine.reset(seed);
    this.controller.reset?.();
    this.accumulatorMs = 0;
    this.lastAdvanceAt = null;
    this.lastSampleTick = -Infinity;
    this.lastScore = 0;
    this.wasAlive = true;
    this.logger.log(EventType.GAME_RESET, {
      tick: 0,
      gameState: this.engine.getState(),
      trialId: this.trialId,
      metadata: { seed },
    });
  }

  setPaused(paused: boolean): void {
    if (this.paused === paused) return;
    this.paused = paused;
    this.accumulatorMs = 0;
    this.lastAdvanceAt = null;
    this.logger.log(paused ? EventType.PAUSED : EventType.RESUMED, {
      tick: this.engine.currentTick,
      gameState: this.engine.getState(),
    });
  }

  /**
   * Advance simulation to catch up with `nowMs`.
   *
   * A fixed-timestep accumulator, not a variable-dt step: with a variable step
   * the same seed and the same inputs would produce different trajectories on
   * different machines, and every comparison in this project would be
   * uninterpretable.
   *
   * @returns the number of ticks simulated.
   */
  advance(nowMs: number): number {
    for (const pump of this.pumps) pump(nowMs);
    this.stimulation.pump(nowMs, this.engine.currentTick);

    if (this.paused) {
      this.lastAdvanceAt = nowMs;
      return 0;
    }

    if (this.lastAdvanceAt === null) {
      this.lastAdvanceAt = nowMs;
      return 0;
    }

    const elapsed = Math.min(nowMs - this.lastAdvanceAt, this.options.maxCatchUpMs);
    this.lastAdvanceAt = nowMs;
    this.accumulatorMs += elapsed;

    let steps = 0;
    while (this.accumulatorMs >= this.dtMs) {
      this.accumulatorMs -= this.dtMs;
      this.tick();
      steps++;
    }
    return steps;
  }

  /**
   * Simulate exactly one tick. Public so headless trials can drive the session
   * without pretending to have a wall clock.
   */
  tick(): GameState {
    const before = this.engine.restamp();
    const output = this.controller.update(before);

    if (output.flap) {
      // Stamped with the tick's wall clock, not the input edge's. They are
      // different moments -- the edge happened somewhere inside the previous
      // frame -- and the gap between them is the simulation's input
      // quantisation, the last stage of the latency budget and the only one
      // this software is entirely responsible for. Timestamping the flap with
      // the edge time would report that stage as identically zero.
      this.logger.log(EventType.FLAP_EXECUTED, {
        tick: before.tick,
        timestamp: before.timestamp,
        simTimeMs: before.simTimeMs,
        gameState: before,
        requestId: (output.metadata?.requestId as string | undefined) ?? undefined,
        trialId: this.trialId,
        metadata: {
          controller: this.controller.id,
          source: output.metadata?.source,
          inputTimestamp: output.timestamp,
          quantisationMs: before.timestamp - output.timestamp,
        },
      });
    }

    const after = this.engine.step(output.flap);
    this.emitDerivedEvents(after);
    return after;
  }

  private emitDerivedEvents(state: GameState): void {
    if (state.score !== this.lastScore) {
      this.lastScore = state.score;
      this.logger.log(EventType.SCORE, {
        tick: state.tick,
        timestamp: state.timestamp,
        simTimeMs: state.simTimeMs,
        gameState: state,
        trialId: this.trialId,
        metadata: { score: state.score },
      });
    }

    if (this.wasAlive && !state.alive) {
      this.wasAlive = false;
      this.logger.log(EventType.GAME_OVER, {
        tick: state.tick,
        timestamp: state.timestamp,
        simTimeMs: state.simTimeMs,
        gameState: state,
        trialId: this.trialId,
        forceState: true,
        metadata: {
          score: state.score,
          survivalMs: state.simTimeMs,
          flaps: this.engine.totalFlaps,
        },
      });
    }

    if (this.options.stateSampleHz > 0) {
      const every = Math.max(1, Math.round(this.engine.config.simHz / this.options.stateSampleHz));
      if (state.tick - this.lastSampleTick >= every) {
        this.lastSampleTick = state.tick;
        this.logger.log(EventType.GAME_SAMPLE, {
          tick: state.tick,
          timestamp: state.timestamp,
          simTimeMs: state.simTimeMs,
          gameState: state,
          trialId: this.trialId,
          forceState: true,
        });
      }
    }
  }

  end(): void {
    this.logger.log(EventType.SESSION_END, {
      tick: this.engine.currentTick,
      gameState: this.engine.getState(),
      metadata: { events: this.logger.count, dropped: this.logger.droppedCount },
    });
  }
}

export function makeSessionId(): string {
  const stamp = new Date().toISOString().replace(/[-:T]/g, '').slice(0, 15);
  // The sub-millisecond part of the performance clock disambiguates two
  // sessions started in the same second, without needing crypto or
  // Math.random() (which is banned project-wide for reproducibility).
  const suffix = Math.floor((now() % 1) * 1e6)
    .toString(36)
    .padStart(4, '0');
  return `s${stamp}-${suffix}`;
}
